/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Custom hook to manage branding settings for the current user
 * @returns {Object} branding state and functions
 */
export function useBranding() {
    const [customLogoPath, setCustomLogoPath] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadBrandingSettings();
    }, []);

    const loadBrandingSettings = async () => {
        try {
            // Check localStorage first for quick loading
            const cachedLogo = localStorage.getItem('custom_logo_path');
            if (cachedLogo) {
                setCustomLogoPath(cachedLogo);
            }

            // Fetch from API to ensure we have latest settings
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/get_branding.php`, {
                withCredentials: true
            });

            if (response.data.success && response.data.settings) {
                const logoPath = response.data.settings.custom_logo_path;
                setCustomLogoPath(logoPath);
                localStorage.setItem('custom_logo_path', logoPath || '');
            }
        } catch (error) {
            // Keep cached value if API fails
        } finally {
            setLoading(false);
        }
    };

    return {
        customLogoPath,
        loading,
        reloadBranding: loadBrandingSettings
    };
}

/**
 * Component to render branding (logo or text) based on user settings
 * @param {Object} props
 * @param {string} props.defaultText - Default text to show if no custom logo
 * @param {string} props.className - CSS class for the branding container
 * @param {boolean} props.showIcon - Whether to show the default icon alongside text
 * @param {string} props.textTag - HTML tag to use for text ('h1', 'h2', 'span', etc.)
 */
export function BrandingDisplay({ defaultText = "Marksure", className = "", showIcon = true, textTag = 'span' }) {
    const { customLogoPath, loading } = useBranding();

    const TextComponent = textTag;

    if (loading) {
        return (
            <div className={`branding-container ${className}`}>
                {showIcon && <div className="logo-placeholder">📊</div>}
                <TextComponent>{defaultText}</TextComponent>
            </div>
        );
    }

    if (customLogoPath) {
        return (
            <div className={`branding-container ${className}`}>
                <img
                    src={`${process.env.REACT_APP_API_URL}/${customLogoPath}`}
                    alt="Custom Logo"
                    className="custom-logo"
                    onError={(e) => {
                        // Fallback to default if image fails to load
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                    }}
                />
                <div className="logo-fallback" style={{ display: 'none' }}>
                    {showIcon && <div className="logo-placeholder">📊</div>}
                    <TextComponent>{defaultText}</TextComponent>
                </div>
            </div>
        );
    }

    return (
        <div className={`branding-container ${className}`}>
            {showIcon && <div className="logo-placeholder">📊</div>}
            <TextComponent>{defaultText}</TextComponent>
        </div>
    );
}